import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  DataSurface,
  PageShell,
  cn,
} from "../design-system";
import "./AgentsPage.css";

const MCP_HOST_URL = "https://mcp.botspot.trade";
const MCP_SERVER_URL = "https://mcp.botspot.trade/mcp";

const codeBlocks = {
  claudeCode: `claude mcp add --transport http botspot https://mcp.botspot.trade/mcp --header "Authorization: Bearer $BOTSPOT_API_KEY"`,
  codex: `[mcp_servers.botspot]
url = "https://mcp.botspot.trade/mcp"
bearer_token_env_var = "BOTSPOT_API_KEY"`,
  gemini: `gemini mcp add --transport http botspot https://mcp.botspot.trade/mcp`,
  cursor: `{
  "mcpServers": {
    "botspot": {
      "url": "https://mcp.botspot.trade/mcp",
      "headers": {
        "Authorization": "Bearer botspot_YOUR_API_KEY"
      }
    }
  }
}`,
};

type ClientId = "chatgpt" | "claude" | "claude-code" | "codex" | "gemini" | "cursor";

interface CopyButtonProps {
  value: string;
  label: string;
  className?: string;
  children?: React.ReactNode;
}

interface StepInstruction {
  label: string;
  body: React.ReactNode;
}

interface ConnectClient {
  id: ClientId;
  name: string;
  context: string;
  auth: string;
  bestFor: string;
  copyValue?: string;
  copyLabel?: string;
  code?: string;
  steps?: StepInstruction[];
}

const connectClients: ConnectClient[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    context: "OAuth connector",
    auth: "OAuth",
    bestFor: "Asking ChatGPT to inspect BotSpot strategies, broker context, backtests, and deployment state.",
    copyValue: MCP_SERVER_URL,
    copyLabel: "Copy ChatGPT Server URL",
    steps: [
      {
        label: "Check access",
        body: <>Use ChatGPT on the web. Your plan and workspace role must allow custom apps; a workspace admin may need to enable <strong>Developer mode</strong> first.</>,
      },
      {
        label: "Create app",
        body: <>Open <strong>Settings -&gt; Apps</strong>, click <strong>Create</strong>, and keep <strong>Server URL</strong> selected. Workspace admins can also use <strong>Workspace settings -&gt; Apps -&gt; Create</strong>.</>,
      },
      {
        label: "Fill fields",
        body: <>Use <strong>BotSpot</strong> as the name, paste <code>{MCP_SERVER_URL}</code>, and set authentication to <strong>OAuth</strong>.</>,
      },
      {
        label: "Scan and authorize",
        body: <>Click <strong>Scan Tools</strong>, complete the BotSpot OAuth login, wait for the scan to finish, then click <strong>Create</strong>.</>,
      },
      {
        label: "Use it in chat",
        body: "Start a new chat and select BotSpot from the tools menu, or refer to BotSpot in your prompt.",
      },
    ],
  },
  {
    id: "claude",
    name: "Claude",
    context: "Remote MCP",
    auth: "OAuth",
    bestFor: "Keeping Claude connected to the same BotSpot strategy and execution context you use in the app.",
    copyValue: MCP_HOST_URL,
    copyLabel: "Copy Claude MCP URL",
    steps: [
      {
        label: "Open Claude",
        body: <>Go to <strong>Settings -&gt; Connectors</strong> and add a custom web connector.</>,
      },
      {
        label: "Authorize",
        body: "Paste the MCP server URL, complete OAuth, then enable the connector for the conversation.",
      },
    ],
  },
  {
    id: "claude-code",
    name: "Claude Code",
    context: "CLI setup",
    auth: "BotSpot API key",
    bestFor: "Working from a terminal while still referencing your BotSpot library, revisions, and backtest evidence.",
    code: codeBlocks.claudeCode,
    copyLabel: "Copy Claude Code command",
    steps: [
      {
        label: "Create a key",
        body: <>Create a BotSpot API key and export it as <code>BOTSPOT_API_KEY</code>.</>,
      },
      {
        label: "Install MCP",
        body: "Run the command below from your shell.",
      },
    ],
  },
  {
    id: "codex",
    name: "Codex",
    context: "CLI, desktop, and IDE setup",
    auth: "BotSpot API key",
    bestFor: "Researching markets and working with your BotSpot strategies, revisions, and backtest evidence from Codex.",
    code: codeBlocks.codex,
    copyLabel: "Copy Codex config",
    steps: [
      {
        label: "Create a key",
        body: <>Create a BotSpot API key and export it as <code>BOTSPOT_API_KEY</code>.</>,
      },
      {
        label: "Configure BotSpot",
        body: <>Add the configuration below to <code>~/.codex/config.toml</code> or a trusted project's <code>.codex/config.toml</code>.</>,
      },
      {
        label: "Confirm the connection",
        body: <>Open Codex and use <code>/mcp</code>, or run <code>codex mcp list</code>.</>,
      },
    ],
  },
  {
    id: "gemini",
    name: "Gemini CLI",
    context: "CLI setup",
    auth: "OAuth",
    bestFor: "Using Gemini CLI with the same BotSpot strategies, broker context, backtests, and trading workflows.",
    code: codeBlocks.gemini,
    copyLabel: "Copy Gemini CLI command",
    steps: [
      {
        label: "Add BotSpot",
        body: "Run the command below to add the hosted BotSpot MCP server.",
      },
      {
        label: "Authorize",
        body: <>Let Gemini CLI discover BotSpot OAuth, then complete the browser login when prompted. Use <code>/mcp auth botspot</code> if authentication is still required.</>,
      },
      {
        label: "Confirm the connection",
        body: <>Run <code>gemini mcp list</code> and check that BotSpot is connected.</>,
      },
    ],
  },
  {
    id: "cursor",
    name: "Cursor",
    context: "IDE setup",
    auth: "BotSpot API key",
    bestFor: "Bringing BotSpot strategy context into a code editor without copying credentials into prompts.",
    code: codeBlocks.cursor,
    copyLabel: "Copy Cursor MCP config",
    steps: [
      {
        label: "Choose scope",
        body: <>Use <code>.cursor/mcp.json</code> for one project or <code>~/.cursor/mcp.json</code> globally.</>,
      },
      {
        label: "Add BotSpot",
        body: "Paste the config below and replace the placeholder with a BotSpot API key.",
      },
    ],
  },
];

function CopyButton({ value, label, className, children }: CopyButtonProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
  }, []);

  const handleCopy = async (): Promise<void> => {
    if (!value || !window.navigator.clipboard) return;

    try {
      await window.navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
      return;
    }

    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Button
      aria-label={label}
      className={cn("agents-copy-button", className)}
      onClick={() => { void handleCopy(); }}
      size="sm"
      type="button"
      variant="outline"
    >
      {copied ? "Copied" : children || "Copy"}
    </Button>
  );
}

function CopyField({ value, label }: CopyButtonProps): React.ReactElement {
  return (
    <div className="agents-copy-field">
      <code>{value}</code>
      <CopyButton value={value} label={label} />
    </div>
  );
}

function CodeBlock({ code, label }: { code: string; label: string }): React.ReactElement {
  return (
    <div className="agents-code-block" role="group" aria-label={`${label} snippet`}>
      <div className="agents-code-block__bar">
        <span>{label}</span>
        <CopyButton value={code} label={`Copy ${label}`} />
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );
}

function ClientPanel({ client }: { client: ConnectClient }): React.ReactElement {
  return (
    <div
      className="agents-client-panel"
      id={`agents-client-${client.id}`}
      role="region"
      aria-label={`${client.name} setup`}
    >
      <div className="agents-client-panel__header">
        <div>
          <span>{client.context}</span>
          <h2>{client.name}</h2>
        </div>
      </div>

      <dl className="agents-client-summary">
        <div>
          <dt>Best for</dt>
          <dd>{client.bestFor}</dd>
        </div>
        <div>
          <dt>Authentication</dt>
          <dd>{client.auth}</dd>
        </div>
      </dl>

      {client.steps && (
        <ol className="agents-step-list">
          {client.steps.map((step) => (
            <li key={step.label}>
              <strong>{step.label}</strong>
              <span>{step.body}</span>
            </li>
          ))}
        </ol>
      )}

      {client.copyValue && client.copyLabel && (
        <div className="agents-connection-block">
          <span>Server URL</span>
          <CopyField value={client.copyValue} label={client.copyLabel} />
        </div>
      )}

      {client.code && client.copyLabel && (
        <CodeBlock code={client.code} label={client.copyLabel.replace("Copy ", "")} />
      )}
    </div>
  );
}

function SetupSurface(): React.ReactElement {
  return (
    <section className="agents-section" aria-label="AI client setup">
      <div className="agents-section__header">
        <div>
          <span>Setup guides</span>
          <h2>Connect from the AI tool you already use</h2>
          <p>Every guide is visible on this page so people and AI assistants can read the complete setup without opening tabs.</p>
        </div>
        <nav className="agents-client-jump-nav" aria-label="Jump to client setup">
          {connectClients.map((client) => (
            <a href={`#agents-client-${client.id}`} key={client.id}>{client.name}</a>
          ))}
        </nav>
      </div>
      <DataSurface className="agents-setup-surface">
        {/* Keep every client in the document. Tabs previously made only ChatGPT visible to crawlers and first-time visitors. */}
        {connectClients.map((client) => (
          <ClientPanel client={client} key={client.id} />
        ))}
      </DataSurface>
    </section>
  );
}

export default function AgentsPage(): React.ReactElement {
  return (
    <PageShell className="agents-page" innerClassName="agents-page__inner">
      <header className="agents-page__header">
        <div>
          <h1>Connect BotSpot to your AI assistant</h1>
          <p>Use BotSpot from ChatGPT, Claude, Claude Code, Codex, Gemini CLI, Cursor, or another MCP-compatible client.</p>
        </div>
        <Button as={Link} to="/account-settings/api-keys" variant="outline">
          Create API key
        </Button>
      </header>
      <SetupSurface />
    </PageShell>
  );
}
